/**
 * The "Google Cloud services" step of the create wizard.
 *
 * The copy is the substance of this file, not the markup. The audience is a
 * citizen developer who cannot tell Cloud Run Jobs from Cloud Scheduler by
 * name, and should not have to — so every service is described by the situation
 * that calls for it ("a report has to run every night"), never by the GCP
 * concept behind it. The labels are task-oriented display copy; stable wire
 * keys continue to carry the GCP-specific capability contract downstream.
 *
 * Examples live in a shared guide in app settings and the create wizard,
 * keeping the selection list focused on the decisions.
 *
 * Order is fixed by the shared contract (wizard, tfvars, PR body, docs all
 * read the same way) — do not sort or regroup these.
 */
import { useEffect, useId, useState } from 'react';

import { Button, Switch, Icon, Tag } from '../ds';
import { icons } from '../icons';
import { c } from '../tokens';
import { CapabilityGuide } from './CapabilityGuide';
import type {
  AppCapabilitySelection,
  AppCloudSqlReadiness,
  CloudSqlRuntimeReconciliation,
  AppServiceKey,
  AppServiceSelection,
} from '../api';
import './AppServicePicker.css';

interface AppService {
  key: AppServiceKey;
  label: string;
  /** Always visible. One sentence, no GCP vocabulary. */
  summary: string;
  /** "You need this if…" situations, behind the disclosure. */
  examples: string[];
  /** Provider product reference shown after the examples. */
  gcpService: string;
}

export const DEFAULT_SCHEDULER_CRON = '0 7 * * *';
export const SCHEDULER_TIME_ZONE = 'America/Los_Angeles';

type SchedulePreset = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'advanced';

const WEEKDAYS = [
  ['1', 'Monday'],
  ['2', 'Tuesday'],
  ['3', 'Wednesday'],
  ['4', 'Thursday'],
  ['5', 'Friday'],
  ['6', 'Saturday'],
  ['0', 'Sunday'],
] as const;

const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const WEEKDAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function validCronField(field: string, min: number, max: number, names: string[] = []): boolean {
  const valueOf = (value: string): number | undefined => {
    const named = names.indexOf(value.toUpperCase());
    if (named >= 0) return min + named;
    if (!/^\d+$/.test(value)) return undefined;
    const numeric = Number(value);
    return numeric >= min && numeric <= max ? numeric : undefined;
  };

  return field.split(',').every(part => {
    const stepParts = part.split('/');
    if (stepParts.length > 2 || !stepParts[0]) return false;
    if (stepParts[1] !== undefined && (!/^\d+$/.test(stepParts[1]) || Number(stepParts[1]) < 1)) return false;
    if (stepParts[0] === '*') return true;
    const range = stepParts[0].split('-');
    if (stepParts[1] !== undefined && range.length !== 2) return false;
    if (range.length > 2 || !range[0] || (range.length === 2 && !range[1])) return false;
    const start = valueOf(range[0]);
    const end = range.length === 2 ? valueOf(range[1]) : start;
    return start !== undefined && end !== undefined && start <= end;
  });
}

export function schedulerCronError(value: string): string | undefined {
  const fields = value.trim().split(/\s+/);
  if (fields.length !== 5) return 'Enter a five-field cron expression.';
  const valid = validCronField(fields[0], 0, 59)
    && validCronField(fields[1], 0, 23)
    && validCronField(fields[2], 1, 31)
    && validCronField(fields[3], 1, 12, MONTH_NAMES)
    && validCronField(fields[4], 0, 6, WEEKDAY_NAMES);
  if (!valid) {
    return 'The expression contains an invalid value or unsupported cron operator.';
  }
  return undefined;
}

function presetForCron(cron: string): SchedulePreset {
  if (schedulerCronError(cron)) return 'advanced';
  const [minute, hour, day, month, weekday] = cron.trim().split(/\s+/);
  if (minute === '0' && hour === '*' && day === '*' && month === '*' && weekday === '*') return 'hourly';
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && day === '*' && month === '*' && weekday === '*') return 'daily';
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && day === '*' && month === '*' && weekday === '1-5') return 'weekdays';
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && day === '*' && month === '*' && /^[0-6]$/.test(weekday)) return 'weekly';
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(day) && month === '*' && weekday === '*') return 'monthly';
  return 'advanced';
}

function timeFromCron(cron: string): string {
  const [minute = '0', hour = '7'] = cron.trim().split(/\s+/);
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

function cronWithTime(preset: SchedulePreset, time: string, cron: string): string {
  const [hour, minute] = time.split(':');
  const fields = cron.trim().split(/\s+/);
  if (preset === 'weekdays') return `${minute} ${hour} * * 1-5`;
  if (preset === 'weekly') return `${minute} ${hour} * * ${fields[4] ?? '1'}`;
  if (preset === 'monthly') return `${minute} ${hour} ${fields[2] ?? '1'} * *`;
  return `${minute} ${hour} * * *`;
}

function SchedulerEditor({
  cron,
  timeZone,
  onChange,
  requiresEdit,
}: {
  cron: string;
  timeZone: string;
  onChange: (cron: string) => void;
  requiresEdit: boolean;
}) {
  const [draftCron, setDraftCron] = useState(cron);
  const [preset, setPreset] = useState<SchedulePreset>(() => presetForCron(cron));
  const [editing, setEditing] = useState(!requiresEdit);
  const [saved, setSaved] = useState(false);
  const displayedCron = requiresEdit ? draftCron : cron;
  const fields = displayedCron.trim().split(/\s+/);
  const time = timeFromCron(displayedCron);
  const error = preset === 'advanced' ? schedulerCronError(displayedCron) : undefined;

  useEffect(() => {
    setDraftCron(cron);
    setPreset(presetForCron(cron));
  }, [cron]);

  useEffect(() => {
    setEditing(!requiresEdit);
    setSaved(false);
  }, [requiresEdit]);

  const updateCron = (next: string): void => {
    setDraftCron(next);
    setSaved(false);
    if (!requiresEdit) onChange(next);
  };

  const selectPreset = (next: SchedulePreset): void => {
    setPreset(next);
    if (next === 'hourly') updateCron('0 * * * *');
    if (next === 'daily') updateCron('0 7 * * *');
    if (next === 'weekdays') updateCron('0 7 * * 1-5');
    if (next === 'weekly') updateCron('0 7 * * 1');
    if (next === 'monthly') updateCron('0 7 1 * *');
  };

  return (
    <div className="cdp-schedule-editor">
      <div className="cdp-schedule-grid">
        <label>
          <span>Runs</span>
          <select
            className="cdp-schedule-control"
            value={preset}
            disabled={!editing}
            onChange={event => selectPreset(event.target.value as SchedulePreset)}
          >
            <option value="hourly">Every hour</option>
            <option value="daily">Every day</option>
            <option value="weekdays">Every weekday</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
            <option value="advanced">Advanced cron expression</option>
          </select>
        </label>

        {preset !== 'hourly' && preset !== 'advanced' && (
          <label>
            <span>At</span>
            <input
              className="cdp-schedule-control"
              type="time"
              value={time}
              disabled={!editing}
              onChange={event => updateCron(cronWithTime(preset, event.target.value, displayedCron))}
            />
          </label>
        )}

        {preset === 'weekly' && (
          <label>
            <span>Day</span>
            <select
              className="cdp-schedule-control"
              value={fields[4] ?? '1'}
              disabled={!editing}
              onChange={event => updateCron(`${fields[0]} ${fields[1]} * * ${event.target.value}`)}
            >
              {WEEKDAYS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        )}

        {preset === 'monthly' && (
          <label>
            <span>Day of month</span>
            <select
              className="cdp-schedule-control"
              value={fields[2] ?? '1'}
              disabled={!editing}
              onChange={event => updateCron(`${fields[0]} ${fields[1]} ${event.target.value} * *`)}
            >
              {Array.from({ length: 28 }, (_, index) => index + 1).map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {preset === 'advanced' && (
        <label className="cdp-schedule-advanced">
          <span>Cron expression</span>
          <input
            className="cdp-schedule-control cdp-schedule-cron"
            value={displayedCron}
            disabled={!editing}
            aria-invalid={Boolean(error)}
            onChange={event => updateCron(event.target.value)}
          />
          <small className={error ? 'cdp-schedule-error' : undefined}>
            {error ?? 'Five fields: minute, hour, day of month, month, day of week. Dates unavailable in a month are skipped.'}
          </small>
        </label>
      )}

      <p className="cdp-schedule-time-zone">
        {timeZone === SCHEDULER_TIME_ZONE
          ? 'The time zone is fixed to Pacific Time (San Francisco) and automatically adjusts for daylight saving time.'
          : `This existing schedule uses ${timeZone}; changing its time zone requires a reviewed infrastructure update.`}
      </p>

      {requiresEdit && (
        <div className="cdp-schedule-actions">
          {editing ? (
            <>
              <button
                type="button"
                className="cdp-schedule-button cdp-schedule-button-primary"
                disabled={Boolean(schedulerCronError(displayedCron)) || displayedCron.trim() === cron.trim()}
                onClick={() => {
                  const next = displayedCron.trim().replace(/\s+/g, ' ');
                  onChange(next);
                  setDraftCron(next);
                  setEditing(false);
                  setSaved(true);
                }}
              >
                Save schedule
              </button>
              <button
                type="button"
                className="cdp-schedule-button"
                onClick={() => {
                  setDraftCron(cron);
                  setPreset(presetForCron(cron));
                  setEditing(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="cdp-schedule-button"
              onClick={() => {
                setEditing(true);
                setSaved(false);
              }}
            >
              Edit schedule
            </button>
          )}
          {saved && (
            <span className="cdp-schedule-saved" role="status">
              Schedule change committed to this request. Select Request capability changes to submit it for review.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export const APP_SERVICES: readonly AppService[] = [
  {
    key: 'cloudRunJobs',
    label: 'Background Jobs',
    summary:
      'Runs a task when you or another system starts it, then stops when the work is finished.',
    examples: [
      'An admin starts a large report and checks back when it is ready.',
      'You need to work through a pile of files once, and then be done.',
      'A deployment starts a database migration that runs until it is complete.',
      'You have a one-off cleanup or a backfill to run over data you already have.',
    ],
    gcpService: 'Cloud Run Jobs',
  },
  {
    key: 'cloudScheduler',
    label: 'Scheduled Tasks',
    summary: 'Automatically starts a Background Job on the days and times you choose.',
    examples: [
      'Every weekday at 7am, refresh the numbers behind a dashboard.',
      'Once an hour, check whether a new file has arrived.',
      'On the first of the month, send a summary out to a mailing list.',
    ],
    gcpService: 'Cloud Scheduler',
  },
  {
    key: 'cloudStorage',
    label: 'File Storage',
    summary: 'Somewhere to keep files your app reads or writes.',
    examples: [
      'People upload a spreadsheet or a PDF and your app reads it.',
      'Your app produces an export that someone downloads later.',
      'A file has to outlive the run that made it — anything written elsewhere is gone when the app restarts.',
      'You need to hand a large file over to another team or system.',
    ],
    gcpService: 'Cloud Storage',
  },
  {
    key: 'bigquery',
    label: 'Data Analytics',
    summary:
      'A very large table you can ask questions of, for data you want to analyze rather than just keep.',
    examples: [
      'You want to run SQL across millions of rows without it grinding to a halt.',
      'You are recording events over time and will want to chart or compare them later.',
      'An analyst, or a dashboard tool, needs to query what your app produces.',
      'The data has outgrown anything a spreadsheet could hold.',
    ],
    gcpService: 'BigQuery',
  },
  {
    key: 'cloudSql',
    label: 'Transactional Database',
    summary: 'A PostgreSQL database for application records and transactions that need fast reads and writes.',
    examples: [
      'Your app stores users, forms, approvals, or workflow state.',
      'Several records need to change together in one transaction.',
      'Your app needs relational queries and constraints rather than analytics over millions of rows.',
    ],
    gcpService: 'Cloud SQL for PostgreSQL',
  },
];

/**
 * Written out rather than derived from `APP_SERVICES` so the contract's key set
 * is checked by the compiler: adding a key to `AppServiceKey` breaks here.
 */
export const NO_APP_SERVICES: AppServiceSelection = {
  cloudRunJobs: false,
  cloudScheduler: false,
  cloudStorage: false,
  bigquery: false,
  cloudSql: false,
};

export const NO_APP_CAPABILITIES: AppCapabilitySelection = {
  secretsManagement: false,
  ...NO_APP_SERVICES,
};

/**
 * Cloud Scheduler cannot stand alone: a schedule starts a Cloud Run Job, and the
 * platform creates that job rather than granting the operator permission to make
 * one. The server enforces this too, but doing it here means the user sees the
 * second toggle move rather than discovering later that the request they
 * submitted is not the one that was provisioned.
 *
 * Turning Cloud Run Jobs back off takes the schedule with it, for the same
 * reason — leaving a clock wired to nothing is not a state worth allowing.
 */
export function applyServiceDependencies(
  value: AppServiceSelection,
  key: AppServiceKey,
  checked: boolean,
): AppServiceSelection {
  const next = { ...value, [key]: checked };
  if (key === 'cloudScheduler' && checked) next.cloudRunJobs = true;
  if (key === 'cloudRunJobs' && !checked) next.cloudScheduler = false;
  return next;
}

/** Display labels for the enabled services, in contract order. */
export function selectedServiceLabels(selection: AppServiceSelection): string[] {
  return APP_SERVICES.filter(service => selection[service.key]).map(service => service.label);
}

export function AppServicePicker({
  value,
  onChange,
  secretsManagement,
  onSecretsManagementChange,
  enabled,
  pending,
  schedulerCron = DEFAULT_SCHEDULER_CRON,
  schedulerTimeZone = SCHEDULER_TIME_ZONE,
  onSchedulerCronChange,
  cloudSqlReadiness,
  cloudSqlReadinessError,
  cloudSqlRuntimeBusy,
  cloudSqlRuntimeResults,
  cloudSqlRuntimeErrors,
  onReconcileCloudSqlRuntime,
  helpPresentation = 'inline',
}: {
  value: AppServiceSelection;
  onChange: (value: AppServiceSelection) => void;
  secretsManagement?: boolean;
  onSecretsManagementChange?: (value: boolean) => void;
  enabled?: AppCapabilitySelection;
  pending?: AppCapabilitySelection;
  schedulerCron?: string;
  schedulerTimeZone?: string;
  onSchedulerCronChange?: (cron: string) => void;
  cloudSqlReadiness?: AppCloudSqlReadiness | null;
  cloudSqlReadinessError?: string | null;
  cloudSqlRuntimeBusy?: 'dev' | 'prod' | null;
  cloudSqlRuntimeResults?: Partial<Record<'dev' | 'prod', CloudSqlRuntimeReconciliation>>;
  cloudSqlRuntimeErrors?: Partial<Record<'dev' | 'prod', string>>;
  onReconcileCloudSqlRuntime?: (environment: 'dev' | 'prod') => void;
  helpPresentation?: 'inline' | 'guide';
}) {
  const [schedulerHelpOpen, setSchedulerHelpOpen] = useState(false);
  const [databaseDetailsOpen, setDatabaseDetailsOpen] = useState(false);
  const databaseDetailsId = useId();
  const rows: readonly (AppService | {
    key: 'secretsManagement';
    label: string;
    summary: string;
    examples: string[];
    gcpService: string;
  })[] = [
    ...(
      onSecretsManagementChange
        ? [{
            key: 'secretsManagement' as const,
            label: 'App Secrets',
            summary: 'Stores sensitive values your app needs without putting them in source code.',
            examples: [
              'Your app calls an external API that requires a private API key.',
              'A webhook or integration gives you a token that must not be committed to GitHub.',
              'Your app needs a password or credential that should be changed independently of a deploy.',
            ],
            gcpService: 'Secret Manager',
          }]
        : []
    ),
    ...APP_SERVICES,
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {helpPresentation === 'guide' && <CapabilityGuide services={rows}
        selection={{ ...value, secretsManagement: Boolean(secretsManagement) }} enabled={enabled} pending={pending}
        onSelect={key => {
          if (enabled?.[key] || pending?.[key]) return;
          if (key === 'secretsManagement') onSecretsManagementChange?.(true);
          else onChange(applyServiceDependencies(value, key, true));
        }} />}
      {rows.map((service, index) => {
        const isSecrets = service.key === 'secretsManagement';
        const isEnabled = Boolean(enabled?.[service.key]);
        const isPending = Boolean(pending?.[service.key]);
        const checked = isEnabled || isPending || (isSecrets ? Boolean(secretsManagement) : value[service.key]);
        const isEnabledCloudSql = service.key === 'cloudSql' && isEnabled;

        return (
        <div
          key={service.key}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 24,
            padding: '18px 0',
            borderBottom:
              index === rows.length - 1 ? 'none' : `1px solid ${c.border}`,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 475, letterSpacing: '0.28px' }}>
                {service.label}
              </span>
              {service.key === 'cloudScheduler' && helpPresentation === 'inline' && (
                <button
                  type="button"
                  className="cdp-capability-help"
                  aria-label={`${schedulerHelpOpen ? 'Hide' : 'Show'} information about Scheduled Tasks`}
                  aria-expanded={schedulerHelpOpen}
                  onClick={() => setSchedulerHelpOpen(open => !open)}
                >
                  ?
                </button>
              )}
              {isEnabled && !isEnabledCloudSql && <Tag variant="success">Enabled</Tag>}
              {isEnabledCloudSql && !cloudSqlReadiness && !cloudSqlReadinessError && <Tag variant="info">Checking</Tag>}
              {isEnabledCloudSql && cloudSqlReadiness?.state === 'ready' && <Tag variant="success">Enabled</Tag>}
              {isEnabledCloudSql && cloudSqlReadiness?.state === 'provisioning' && <Tag variant="warning">Provisioning</Tag>}
              {isEnabledCloudSql && (cloudSqlReadiness?.state === 'error' || cloudSqlReadinessError) && (
                <Tag variant="warning">Check failed</Tag>
              )}
              {!isEnabled && isPending && <Tag variant="warning">Pending review</Tag>}
            </div>
            {service.key === 'cloudScheduler' && schedulerHelpOpen && (
              <p className="cdp-capability-help-text">
                Scheduled Tasks start a separate Background Job; they do not schedule or restart
                your main web app. For example, refresh dashboard data every morning while the app
                remains available to users.
              </p>
            )}
            <span
              style={{
                fontSize: 13,
                lineHeight: 1.4,
                color: c.textMuted,
                maxWidth: '52ch',
                textWrap: 'pretty',
              }}
            >
              {service.summary}
            </span>

            {isEnabledCloudSql && (cloudSqlReadiness || cloudSqlReadinessError) && <>
              <button type="button" className="cdp-database-details-toggle"
                aria-expanded={databaseDetailsOpen} aria-controls={databaseDetailsId}
                onClick={() => setDatabaseDetailsOpen(open => !open)}>
                {databaseDetailsOpen ? 'Hide details' : 'Show details'}
                <Icon name="chevron-down" size="sm" />
              </button>
            {databaseDetailsOpen && (
              <div
                id={databaseDetailsId}
                role="status"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  color: c.textMuted,
                }}
              >
                {cloudSqlReadiness?.environments.map(environment => {
                  const result = cloudSqlRuntimeResults?.[environment.environment];
                  const runtimeError = cloudSqlRuntimeErrors?.[environment.environment];
                  return (
                    <div
                      key={environment.environment}
                      style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}
                    >
                      <span>
                        <strong>{environment.environment.toUpperCase()} ({environment.state}):</strong> {environment.message}
                      </span>
                      <span>Project: <code>{environment.projectId}</code></span>
                      {(Object.entries(environment.runtimeEnvironment) as [string, string][]).map(([name, value]) => (
                        <span key={name}><code>{name}={value}</code></span>
                      ))}
                      <span>
                        Runtime variables: <strong>{environment.runtimeConfiguration.ready ? 'configured' : 'not configured'}</strong>
                      </span>
                      {result && (
                        <span>
                          Applied to service <code>{result.service.name}</code>
                          {result.job ? <> and job <code>{result.job.name}</code></> : '; no Cloud Run Job is provisioned'}.
                        </span>
                      )}
                      {runtimeError && <span style={{ color: c.brandLink }}>{runtimeError}</span>}
                      {onReconcileCloudSqlRuntime && (
                        <div>
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={environment.state !== 'ready' || Boolean(cloudSqlRuntimeBusy)}
                            onPress={() => onReconcileCloudSqlRuntime(environment.environment)}
                          >
                            {cloudSqlRuntimeBusy === environment.environment ? 'Applying...' : 'Apply to Cloud Run'}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {cloudSqlReadinessError && <span>{cloudSqlReadinessError}</span>}
              </div>
            )}
            </>}

            {service.key === 'cloudScheduler' && checked && onSchedulerCronChange && (
              <SchedulerEditor
                cron={schedulerCron}
                timeZone={schedulerTimeZone}
                onChange={onSchedulerCronChange}
                requiresEdit={isEnabled || isPending}
              />
            )}

            {helpPresentation === 'inline' && <details className="cdp-disclosure">
              <summary
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: 'fit-content',
                  padding: '2px 0',
                  fontSize: 13,
                  fontWeight: 475,
                  letterSpacing: '0.26px',
                  color: c.brandLink,
                  cursor: 'pointer',
                }}
              >
                <Icon
                  name={icons.chevronRight}
                  size="sm"
                  color={c.brandLink}
                  className="cdp-disclosure-chevron"
                />
                <span>When do I need this?</span>
              </summary>
              <ul
                style={{
                  margin: '8px 0 0',
                  padding: '0 0 0 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: c.textBody,
                  maxWidth: '58ch',
                  textWrap: 'pretty',
                }}
              >
                {service.examples.map(example => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
              <p
                style={{
                  margin: '10px 0 0',
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  color: c.textFaint,
                  maxWidth: '58ch',
                  textWrap: 'pretty',
                }}
              >
                GCP service: {service.gcpService}
              </p>
            </details>}
          </div>

          <Switch
            checked={checked}
            disabled={isEnabled || isPending}
            onCheckedChange={nextChecked => {
              if (isSecrets) onSecretsManagementChange?.(nextChecked);
              else onChange(applyServiceDependencies(value, service.key, nextChecked));
            }}
            aria-label={`Enable ${service.label}`}
          />
        </div>
        );
      })}
    </div>
  );
}
